import {ConfigInterface} from "../../config";

export abstract class APIService {
  constructor(
    protected readonly config: ConfigInterface,
    protected readonly tokenResolver: () => string|null,
    protected readonly navigate: (route: string) => void,
  ) {
  }

  protected async checkForValidationErrors(response: Response): Promise<void> {
    if (response.status === 403) {
      this.navigate('/forbidden')
      throw new Error('You do not have permission to perform this action.')
    }
    if (response.status === 422) {
      const json = await response.json()
      if ("message" in json && "errors" in json && typeof json.errors === "object") {
        throw new ValidationError(json.message, json.errors)
      }
      if ("message" in json) {
        throw new ValidationError(json.message, {'message': [json.message]})
      }
      if ("error" in json) {
        throw new ValidationError(json.error, {'message': [json.error]})
      }
    }
    if (response.status === 500) {
      throw new Error('Onverwachte fout')
    }
  }

  protected async getJson<T>(url: string): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      }
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }

  protected async getRaw(url: string): Promise<Response> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      headers: {
        'Authorization': `Bearer ${this.tokenResolver()}`
      }
    })
    await this.checkForValidationErrors(response)
    return response;
  }

  protected async postJson<T>(url: string, body: object): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: JSON.stringify(body)
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }

  protected async postRaw<T>(url: string, body: object) {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: JSON.stringify(body)
    })
    await this.checkForValidationErrors(response)
    return response;
  }

  protected async putJson<T>(url: string, body: object): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: JSON.stringify(body)
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }

  protected async patchJson<T>(url: string, body: object): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: JSON.stringify(body)
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }


  protected async deleteJson<T>(url: string): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      }
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }

  protected async postForm<T>(url: string, body: FormData): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: body
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }

  protected async putForm<T>(url: string, body: FormData): Promise<T> {
    const response = await fetch(`${this.config.apiBaseUrl}/${url}`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.tokenResolver()}`
      },
      body: body
    })
    await this.checkForValidationErrors(response)
    return await response.json() as T
  }
}

export type ErrorBag = { [key: string]: string[] }
export class ValidationError extends Error {
  constructor(public readonly message: string, public readonly errors: ErrorBag) {
    super(message);
  }
}